import { useState } from 'react';

import { Box, Heading, Icon, Paragraph, TextButton, Upsell } from '@zillow/constellation-10';
import { IconCameraExposureFilled } from '@zillow/constellation-icons';

import { BannerBox } from '../../../PropertiesPage/styles';

/** sessionStorage key – cleared when tab/window closes, so banner reappears in new tabs and on next login */
const SPOTLIGHT_LISTINGS_BANNER_DISMISSED_KEY = 'ZRM_hasDismissedSpotlightListingsBanner';

const SPOTLIGHT_CONTENT_COPY =
  'Listing Spotlight gives single-family listings on average 2x more exposure than Feed Connect listings, according to Zillow internal data.';

const SpotlightListingsBanner = ({
  hasSpotlightedListings,
  availableSpotlightListings = 0,
}: {
  hasSpotlightedListings: boolean;
  availableSpotlightListings?: number;
}) => {
  const [isDismissed, setIsDismissed] = useState(() =>
    typeof window !== 'undefined' ? sessionStorage.getItem(SPOTLIGHT_LISTINGS_BANNER_DISMISSED_KEY) === 'true' : false,
  );

  const handleDismiss = () => {
    sessionStorage.setItem(SPOTLIGHT_LISTINGS_BANNER_DISMISSED_KEY, 'true');
    setIsDismissed(true);
  };

  if (isDismissed) {
    return null;
  }

  // Not a spotlight customer, show "Spotlight what matters most" / learn more variant
  if (!hasSpotlightedListings) {
    return (
      <BannerBox isEnterpriseCustomer={true}>
        <Upsell
          action={
            <TextButton asChild>
              <a target="_blank" rel="noopener noreferrer" href="/rentals-network/advertise-zillow-rentals-zram/">
                Learn more
              </a>
            </TextButton>
          }
          body={<Paragraph>{SPOTLIGHT_CONTENT_COPY}</Paragraph>}
          header={<Heading level={2}>Spotlight what matters most</Heading>}
          media={
            <Box css={{ color: 'icon.accent.blue.hero', display: 'flex' }}>
              <Icon size="xl" render={<IconCameraExposureFilled />} />
            </Box>
          }
          tone="neutral"
          onOpenChange={(open) => {
            if (!open) {
              handleDismiss();
            }
          }}
        />
      </BannerBox>
    );
  }

  // Eligible but no available placements: don't show the "X available" variant
  if (availableSpotlightListings <= 0) {
    return null;
  }

  const isPlural = availableSpotlightListings > 1;

  return (
    <BannerBox isEnterpriseCustomer={true}>
      <Upsell
        action={<></>}
        body={<Paragraph>{SPOTLIGHT_CONTENT_COPY}</Paragraph>}
        header={
          <Heading level={2}>
            You have {availableSpotlightListings} Listing Spotlight placement
            {isPlural ? 's' : ''} available. Don’t let {isPlural ? 'them' : 'it'} go to waste!
          </Heading>
        }
        media={
          <Box css={{ color: 'icon.accent.blue.hero', display: 'flex' }}>
            <Icon size="xl" render={<IconCameraExposureFilled />} />
          </Box>
        }
        tone="neutral"
        onOpenChange={(open) => {
          if (!open) {
            handleDismiss();
          }
        }}
      />
    </BannerBox>
  );
};

export default SpotlightListingsBanner;
